import '../../styles/AssetLibrary.css';

import { AssetMetadata, AssetOwnerCategory } from '../../models/Asset';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import AlertDialog from '../AlertDialog';
import AssetService from '../../services/AssetService';
import { AssetUploading } from '../../hooks/useAssetStorage';
import AuthService from '../../services/AuthService';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgressWithValue } from '../CircularProgressWithValue';
import { CustomLoadingProgress } from '../../components/CustomLoadingProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { fileSize } from 'humanize-plus';
import { fromMime } from '../../services/MimeTypes';

interface AssetCardProps {
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  category: AssetOwnerCategory;
  lazyLoad: boolean;
  asset: AssetUploading;
  selectedAssetkey: string;
  onUploadCompleted: (assetkey: string) => void;
  onUploadCanceled: (assetkey: string) => void;
  onUploadError: (assetkey: string) => void;
  onEncodeCompleted: (assetMetadata: AssetMetadata) => void;
  onEncodeError: (assetkey: string) => void;
  handleClick: (assetkey: string, endSelection?: boolean) => void;
  handleDelete: (asset: AssetMetadata) => void;
}

export const AssetCard = (props: AssetCardProps) => {
  const imageFrameStyle: CSSProperties = {
    width: `${props.thumbnailWidth || 200}px`,
    height: `${props.thumbnailHeight || 120}px`
  };

  const titleStyle: CSSProperties = {
    width: `${props.thumbnailWidth || 200}px`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  };

  const [progress, setProgress] = useState(0);
  const [assetUrl, setAssetUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string>();

  const checkTimerId = useRef(-1);
  const assetMetadata = useRef<AssetMetadata>(null);
  const userkey = AuthService.Userkey;

  useEffect(() => {
    const { assetkey } = props.asset.metadata;
    getAssetUrl(props.category, assetkey).then(setAssetUrl);
    if (!props.asset.uploadObservable) {
      return;
    }
    const subscription = props.asset.uploadObservable.subscribe({
      next: (progress) => {
        setProgress(progress.value);
      },
      error: (_) => {
        props.onUploadError(assetkey);
        subscription.unsubscribe();
      },
      complete: () => {
        props.onUploadCompleted(assetkey);
        subscription.unsubscribe();
      }
    });
  }, [props.asset]);

  useEffect(() => {
    if (!props.asset.encodeObservable) {
      return;
    }

    const subscription = props.asset.encodeObservable.subscribe({
      next: (timerIdOrAssetMetadata) => {
        if (typeof timerIdOrAssetMetadata === 'number') {
          checkTimerId.current = timerIdOrAssetMetadata;
        } else {
          assetMetadata.current = timerIdOrAssetMetadata;
        }
      },
      error: (_) => {
        window.clearInterval(checkTimerId.current);
        props.onEncodeError(props.asset.metadata.assetkey);
        subscription.unsubscribe();
      },
      complete: () => {
        window.clearInterval(checkTimerId.current);
        props.onEncodeCompleted(assetMetadata.current);
        subscription.unsubscribe();
      }
    });
  }, [props.asset.encodeObservable]);

  async function getAssetUrl(
    category: AssetOwnerCategory,
    assetkey: string,
    rawUrl: boolean = false
  ) {
    const key =
      contentType.startsWith('video') && !rawUrl
        ? AssetService.getVideoThumbnailAssetkey(assetkey)
        : assetkey;

    const url =
      category === 'author'
        ? AssetService.getAssetUrl(key)
        : await AssetService.getLearnerAssetUrl(
            props.asset.metadata.owner,
            key
          );
    return url;
  }

  const {
    assetkey,
    contentType,
    filename: filenameenc,
    size,
    owner
  } = props.asset.metadata;
  const filename = filenameenc && decodeURIComponent(filenameenc);

  return (
    <>
      {props.asset.uploadObservable || props.asset.encodeObservable ? (
        <div className="assetContainer" style={imageFrameStyle}>
          <div className="verticallyCentered">
            <Grid container direction="column" justifyContent="space-evenly">
              <Grid>
                <CircularProgressWithValue
                  value={props.asset.uploadObservable ? progress : null}
                />
              </Grid>
              <Grid>
                {props.asset.uploadObservable ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onUploadCanceled(assetkey);
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Typography variant="caption">Processing...</Typography>
                )}
              </Grid>
            </Grid>
          </div>
        </div>
      ) : (
        <Grid container direction="column">
          <Grid style={titleStyle}>
            <Tooltip title={filename || ''} arrow>
              <Typography variant="caption">{filename || ' '}</Typography>
            </Tooltip>
          </Grid>
          <Grid>
            <div
              className="assetContainer"
              style={imageFrameStyle}
              onClick={(event) => {
                event.stopPropagation();
                props.handleClick(props.asset.metadata.assetkey);
              }}
              onDoubleClick={(event) => {
                event.stopPropagation();
                props.handleClick(props.asset.metadata.assetkey, true);
              }}
            >
              {contentType?.startsWith('image') ||
              contentType?.startsWith('video') ? (
                assetUrl ? (
                  <LazyLoadImage
                    alt="" // to not show a broken icon on 404 error
                    src={assetUrl}
                    effect={'opacity'}
                    width="auto"
                    height={imageFrameStyle.height}
                    visibleByDefault={!props.lazyLoad}
                  />
                ) : (
                  <CustomLoadingProgress />
                )
              ) : (
                <div className="verticallyCentered">
                  <DescriptionIcon fontSize="large" />
                  <div>{fromMime(contentType)?.label || ''}</div>
                </div>
              )}
              {assetkey === props.selectedAssetkey && (
                <CheckCircleIcon
                  className="verticallyCentered"
                  color="primary"
                  fontSize="large"
                />
              )}
              {contentType.startsWith('video') && (
                <IconButton
                  style={{ position: 'absolute', left: 0, bottom: 0 }}
                  title="Preview video"
                  onClick={() =>
                    getAssetUrl(props.category, assetkey, true).then(
                      setPreviewUrl
                    )
                  }
                >
                  <PlayArrowIcon color="primary" />
                </IconButton>
              )}
              {userkey === owner && (
                <IconButton
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    props.handleDelete(props.asset.metadata);
                  }}
                >
                  <DeleteIcon color="primary" />
                </IconButton>
              )}
            </div>
          </Grid>
          <Grid>
            <Typography variant="caption" align="right">
              {size ? fileSize(size) : ' '}
            </Typography>
          </Grid>
          {previewUrl && (
            <AlertDialog
              title={decodeURIComponent(props.asset.metadata.filename)}
              message={
                <video
                  controls
                  src={previewUrl}
                  style={{ width: '100%', maxHeight: 320 }}
                />
              }
              onClose={() => setPreviewUrl(undefined)}
            />
          )}
        </Grid>
      )}
    </>
  );
};
